import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 p-12 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #0ea5e9 0%, transparent 45%), radial-gradient(circle at 80% 70%, #10b981 0%, transparent 40%)" }} />
        <div className="relative flex items-center gap-3">
          <div className="flex items-center justify-center rounded-xl bg-white px-4 py-3 shadow-sm">
            <img src="/sms-logo.jpg" alt="SMS SRL — Servizi Finanziari" className="h-20 w-auto" />
          </div>
        </div>
        <div className="relative space-y-5">
          <h1 className="font-heading text-4xl font-bold leading-tight">Recupero password</h1>
        </div>
        <p className="relative text-xs text-slate-500">© 2026 FinPractice Pro — Milano</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm space-y-8" data-testid="forgot-password-page">
          <div>
            <h2 className="font-heading text-2xl font-bold">Password dimenticata?</h2>
            <p className="text-sm text-muted-foreground mt-1">Inserisci la tua email: ti invieremo un link per reimpostare la password.</p>
          </div>

          {sent ? (
            <div className="space-y-4">
              <div className="flex items-start gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3" data-testid="forgot-password-sent">
                <MailCheck className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                <p className="text-sm text-foreground">Se l'indirizzo è registrato, riceverai a breve un'email con le istruzioni per reimpostare la password. Controlla anche la cartella spam.</p>
              </div>
              <Button variant="outline" className="w-full" data-testid="forgot-back-login" onClick={() => navigate("/login")}><ArrowLeft className="h-4 w-4 mr-2" />Torna al login</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input id="email" data-testid="forgot-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@azienda.it" required />
              </div>
              {error && <p className="text-sm text-destructive" data-testid="forgot-error">{error}</p>}
              <Button type="submit" data-testid="forgot-submit-button" className="w-full" disabled={busy}>{busy ? "Invio…" : "Invia link di recupero"}</Button>
              <button type="button" onClick={() => navigate("/login")} className="w-full text-sm text-muted-foreground hover:text-foreground inline-flex items-center justify-center gap-1.5" data-testid="forgot-back-login-link"><ArrowLeft className="h-4 w-4" />Torna al login</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
